import { createContext, useContext, useMemo } from 'react';

import { SongPreview } from '~/interfaces';
import { genreKey, isPlaceholderGenre, useGenreList } from '~/modules/songs/hooks/use-genre-list';

/**
 * The genre a song is worth showing, or nothing at all.
 *
 * "None" and its siblings are in the genre field of 207 songs and name no genre, so a chip drawn
 * from them says nothing while taking the room of something that does.
 */
export const songGenre = (genre: string | undefined) => {
  const written = genre?.trim();

  return written && !isPlaceholderGenre(written) ? written : undefined;
};

/**
 * The name each genre goes by on this screen, keyed by its collapsed spelling.
 *
 * Empty by default, which means a song is labelled with the spelling its own file carries - right
 * for a screen showing one song, and not enough for a list, where 58 cards saying K-Pop next to 43
 * saying Kpop read as two different genres.
 */
export const GenreLabelsContext = createContext<Map<string, string>>(new Map());

/** Built from the catalogue the screen can reach, so the commonest spelling of a genre is the one shown. */
export const useGenreLabels = (list: Partial<Pick<SongPreview, 'genre'>>[]) => {
  // Every spelling counts, not just the ones with enough songs for a filter of their own: a card
  // has to be labelled whether or not its genre is common enough to be filtered by.
  const genres = useGenreList(list, 1);

  return useMemo(() => new Map(genres.map(({ name }) => [genreKey(name), name])), [genres]);
};

export const useGenreLabel = (genre: string | undefined) => {
  const labels = useContext(GenreLabelsContext);
  const written = songGenre(genre);

  if (!written) return undefined;

  return labels.get(genreKey(written)) ?? written;
};
